import { useMutation, useQuery } from "@apollo/client";
import { ExclamationIcon, SearchIcon } from "@heroicons/react/solid";
import githubLogo from "@static/img/github.png";
import linearLogo from "@static/img/linear.png";
import salesforceLogo from "@static/img/salesforce.png";
import slackLogo from "@static/img/slack.png";
import teamsLogo from "@static/img/teams-64.png";
import youtrackLogo from "@static/img/youtrack.svg";
import {
  ChangeEvent,
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import useLabel from "@apps/use-label/use-label";
import { errorNotificationVar, successNotificationVar } from "@cache/cache";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import Input from "@components/input/input";
import Link from "@components/link/link";
import Modal from "@components/modal/modal";
import ModalTitle from "@components/modal/modal-title";
import { classNames } from "@helpers/css";
import { assertNonNull } from "@helpers/helpers";

import {
  GetMergeDevIntegrationsQuery,
  GetMergeDevIntegrationsQueryVariables,
  GetOrganizationIntegrationsQuery,
  GetOrganizationIntegrationsQueryVariables,
  GetSlackConfigurationQuery,
  GetSlackConfigurationQueryVariables,
  IntegrationTypes,
} from "../../../types/graphql-schema";
import getMergeDevIntegrationsQuery from "../graphql/get-merge-dev-integrations";
import getOrganizationIntegrations from "../graphql/get-organization-integrations";
import getSlackConfiguration from "../graphql/get-slack-configuration";
import removeGithubFromOrganizationMutation from "../graphql/remove-github-from-organization-mutation";
import removeMergeDevIntegrationMutation from "../graphql/remove-merge-dev-integration";
import removeSlackIntegrationMutation from "../graphql/remove-slack-integration-mutation";
import removeTeamsIntegrationMutation from "../graphql/remove-teams-integration-mutation";
import removeYouTrackIntegrationMutation from "../graphql/remove-youtrack-integration-mutation";
import saveFellowIntegrationMutation from "../graphql/save-fellow-integration-mutation";
import unlinkOrganizationFromIntegrationMutation from "../graphql/unlink-organization-from-integration-mutation";
import { getNativeIntegrationsConfig } from "./integrations/integrations-config";
import IntegrationsConnectedTable from "./integrations/integrations-connected-table";
import {
  ALL_INTEGRATIONS_CATEGORY,
  getIntegrationCategoryLabel,
  sortIntegrationCategories,
} from "./integrations/integrations-constants";
import { IntegrationsMarketMerge } from "./integrations/integrations-market-merge";
import { IntegrationsMarketNative } from "./integrations/integrations-market-native";
import {
  ConnectedIntegration,
  MergeIntegration,
  NativeIntegration,
} from "./integrations/integrations-types";
import SlackPermissionsDialog, {
  type SlackPermissions,
} from "./slack-permissions-dialog";

const FellowIntegrationDialog = ({
  open,
  initialApiDomain,
  isSaving,
  onClose,
  onSave,
}: {
  open: boolean;
  initialApiDomain?: string | null;
  isSaving: boolean;
  onClose: () => void;
  onSave: (apiKey: string, apiDomain: string) => void;
}) => {
  const [apiKey, setApiKey] = useState("");
  const [apiDomain, setApiDomain] = useState(initialApiDomain || "");

  useEffect(() => {
    if (open) {
      setApiKey("");
      setApiDomain(initialApiDomain || "");
    }
  }, [initialApiDomain, open]);

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    onSave(apiKey.trim(), apiDomain.trim());
  };

  return (
    <Modal open={open} onClose={onClose}>
      <form onSubmit={handleSubmit} className="p-6">
        <ModalTitle onClose={onClose}>Connect to Fellow</ModalTitle>
        <div className="mt-6 space-y-4">
          <p className="text-sm text-gray-600">
            Enter the Fellow API key and API domain for this organization.
            Topicflow will use them to import Fellow meeting transcripts.
          </p>
          <Input
            autoFocus
            label="API key"
            name="fellow-api-key"
            type="password"
            value={apiKey}
            onChange={(event) => setApiKey(event.target.value)}
            autoComplete="off"
          />
          <Input
            label="API domain"
            name="fellow-api-domain"
            type="url"
            value={apiDomain}
            onChange={(event) => setApiDomain(event.target.value)}
            placeholder="https://api.fellow.app"
            autoComplete="off"
          />
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <Button onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            type="submit"
            theme={buttonTheme.primary}
            disabled={isSaving || !apiKey.trim() || !apiDomain.trim()}
          >
            Save
          </Button>
        </div>
      </form>
    </Modal>
  );
};

type MarketplaceData = {
  categories: readonly string[];
  ready: boolean;
  visibleCount: number;
};

const defaultMarketplaceData: MarketplaceData = {
  categories: [],
  ready: false,
  visibleCount: 0,
};

const isMarketplaceDataEqual = (
  current: MarketplaceData,
  next: MarketplaceData,
) =>
  current.ready === next.ready &&
  current.visibleCount === next.visibleCount &&
  current.categories.join("|") === next.categories.join("|");

const Integrations = ({ organization }: { organization: any }) => {
  const label = useLabel();
  const [showSlackPermissionsDialog, setShowSlackPermissionsDialog] =
    useState(false);
  const [showFellowIntegrationDialog, setShowFellowIntegrationDialog] =
    useState(false);
  const [activeMarketplaceCategory, setActiveMarketplaceCategory] = useState(
    ALL_INTEGRATIONS_CATEGORY,
  );
  const [marketplaceSearchQuery, setMarketplaceSearchQuery] = useState("");
  const [nativeMarketplaceData, setNativeMarketplaceData] =
    useState<MarketplaceData>(defaultMarketplaceData);
  const [mergeMarketplaceData, setMergeMarketplaceData] =
    useState<MarketplaceData>(defaultMarketplaceData);
  const [slackPermissions, setSlackPermissions] = useState<SlackPermissions>({
    notifications: false,
    bot: false,
    ingest: false,
  });
  const slackConnectUrl = useMemo(
    () =>
      `/sso/slack?next=/settings/organization/${organization.id}/integrations&org=${organization.id}`,
    [organization.id],
  );

  // ---------------------------------------------------------------------------
  // Queries and mutations for connected integrations
  // ---------------------------------------------------------------------------
  const { data: orgIntegrationsData, refetch: refetchOrgIntegrations } =
    useQuery<
      GetOrganizationIntegrationsQuery,
      GetOrganizationIntegrationsQueryVariables
    >(getOrganizationIntegrations, {
      variables: { organizationId: organization.id },
    });

  const { data: mergeIntegrationsData, refetch: refetchMergeIntegrations } =
    useQuery<
      GetMergeDevIntegrationsQuery,
      GetMergeDevIntegrationsQueryVariables
    >(getMergeDevIntegrationsQuery, {
      variables: { organizationId: organization.id },
    });
  const { data: slackConfigData } = useQuery<
    GetSlackConfigurationQuery,
    GetSlackConfigurationQueryVariables
  >(getSlackConfiguration, {
    variables: { organizationId: organization.id },
  });

  const [unlinkOrganizationFromIntegration] = useMutation(
    unlinkOrganizationFromIntegrationMutation,
  );
  const [removeSlackIntegration] = useMutation(removeSlackIntegrationMutation);
  const [removeGithub] = useMutation(removeGithubFromOrganizationMutation);
  const [removeTeamsIntegration] = useMutation(removeTeamsIntegrationMutation, {
    onCompleted: () => {
      successNotificationVar({ title: "Teams integration removed" });
      handleRefetch();
    },
  });
  const [removeYouTrackIntegration] = useMutation(
    removeYouTrackIntegrationMutation,
    {
      refetchQueries: ["getOrganizationSettings"],
      onCompleted: () => {
        successNotificationVar({ title: "YouTrack integration removed" });
      },
    },
  );
  const [removeMergeDevIntegration] = useMutation(
    removeMergeDevIntegrationMutation,
    {
      onCompleted: () => {
        successNotificationVar({ title: "Integration removed" });
        handleRefetch();
      },
    },
  );
  const [saveFellowIntegration, { loading: isSavingFellowIntegration }] =
    useMutation(saveFellowIntegrationMutation, {
      refetchQueries: ["getOrganizationSettings"],
    });

  // ---------------------------------------------------------------------------
  // Refetch handler for child components
  // ---------------------------------------------------------------------------
  const handleRefetch = useCallback(() => {
    refetchOrgIntegrations();
    refetchMergeIntegrations();
  }, [refetchOrgIntegrations, refetchMergeIntegrations]);

  // ---------------------------------------------------------------------------
  // Build connected native integrations list
  // ---------------------------------------------------------------------------
  const connectedNativeIntegrations: NativeIntegration[] = useMemo(() => {
    const nativeIntegrationsConfig = getNativeIntegrationsConfig(
      organization,
      label,
    );
    const result: NativeIntegration[] = [];
    const slackLastIndexedTs = (() => {
      const channels =
        slackConfigData?.organization?.slackIndexingConfiguration?.channels ||
        [];
      const timestamps = channels
        .map((channel) => channel?.lastIndexedTs)
        .filter((ts): ts is string => Boolean(ts))
        .map((ts) => {
          const date = new Date(ts);
          return Number.isNaN(date.getTime())
            ? null
            : { ts, time: date.getTime() };
        })
        .filter((item): item is { ts: string; time: number } => Boolean(item));
      if (!timestamps.length) {
        return undefined;
      }
      return timestamps.reduce((latest, current) =>
        current.time > latest.time ? current : latest,
      ).ts;
    })();

    const salesforceIntegrations =
      orgIntegrationsData?.organization?.integrations.edges
        .filter(
          (edge) => edge?.node?.integrationType === IntegrationTypes.Salesforce,
        )
        .map((edge) => assertNonNull(edge?.node)) ?? [];
    const linearIntegrations =
      orgIntegrationsData?.organization?.integrations.edges
        .filter(
          (edge) => edge?.node?.integrationType === IntegrationTypes.Linear,
        )
        .map((edge) => assertNonNull(edge?.node)) ?? [];

    if (organization.hasSlackConnection) {
      result.push({
        id: "slack",
        type: "native",
        name: "Slack",
        slug: "slack",
        logo: slackLogo,
        description: nativeIntegrationsConfig.slack.description,
        isConnected: true,
        connectedAt: slackLastIndexedTs,
        configureUrl: waffle.flag_is_active("new-slack-settings")
          ? `/settings/organization/${organization.id}/slack`
          : undefined,
        onConnect: () => {},
        onDisconnect: () => {
          removeSlackIntegration({
            variables: { organizationId: organization.id },
          });
        },
      });
    }

    if (salesforceIntegrations.length > 0) {
      result.push({
        id: "salesforce",
        type: "native",
        name: "Salesforce",
        slug: "salesforce",
        logo: salesforceLogo,
        description: nativeIntegrationsConfig.salesforce.description,
        isConnected: true,
        connectedAt: salesforceIntegrations[0]?.created,
        onConnect: () => {},
        onDisconnect: () => {
          if (salesforceIntegrations[0]?.id) {
            unlinkOrganizationFromIntegration({
              variables: {
                organizationIntegrationId: salesforceIntegrations[0].id,
              },
            });
          }
        },
      });
    }

    if (
      organization.hasTeamsTranscriptConnection ||
      organization.hasTeamsInstallation
    ) {
      result.push({
        id: "teams",
        type: "native",
        name: "Teams",
        slug: "teams",
        logo: teamsLogo,
        description: nativeIntegrationsConfig.teams.description,
        isConnected: true,
        onConnect: () => {},
        onDisconnect: () => {
          removeTeamsIntegration({
            variables: { organizationId: organization.id },
          });
        },
      });
    }

    if (organization.hasGithubInstallation) {
      result.push({
        id: "github",
        type: "native",
        name: "GitHub",
        slug: "github",
        logo: githubLogo,
        description: nativeIntegrationsConfig.github.description,
        isConnected: true,
        onConnect: () => {},
        onDisconnect: () => {
          removeGithub({
            variables: { organizationId: organization.id },
            onCompleted: () => {
              successNotificationVar({ title: "GitHub integration removed" });
            },
          });
        },
      });
    }

    if (organization.hasFellowTranscriptSyncConnection) {
      result.push({
        id: "fellow",
        type: "native",
        name: "Fellow",
        slug: "fellow",
        logo: nativeIntegrationsConfig.fellow.logo,
        description: nativeIntegrationsConfig.fellow.description,
        isConnected: true,
        onConnect: () => {},
        onConfigure: () => setShowFellowIntegrationDialog(true),
        onDisconnect: () => {
          saveFellowIntegration({
            variables: {
              organizationId: organization.id,
              fellowApiKey: "",
              fellowApiDomain: "",
              fellowTranscriptSyncEnabled: false,
            },
            onCompleted: () => {
              successNotificationVar({ title: "Fellow integration removed" });
            },
            onError: (error) => {
              errorNotificationVar({ title: error.message });
            },
          });
        },
      });
    }

    if (linearIntegrations.length > 0) {
      result.push({
        id: "linear",
        type: "native",
        name: "Linear",
        slug: "linear",
        logo: linearLogo,
        description: nativeIntegrationsConfig.linear.description,
        isConnected: true,
        connectedAt: linearIntegrations[0]?.created,
        onConnect: () => {},
        onDisconnect: () => {
          if (linearIntegrations[0]?.id) {
            unlinkOrganizationFromIntegration({
              variables: {
                organizationIntegrationId: linearIntegrations[0].id,
              },
            });
          }
        },
      });
    }

    if (organization.hasYoutrackIntegration) {
      result.push({
        id: "youtrack",
        type: "native",
        name: "YouTrack",
        slug: "youtrack",
        logo: youtrackLogo,
        description: nativeIntegrationsConfig.youtrack.description,
        isConnected: true,
        onConnect: () => {},
        onDisconnect: () => {
          removeYouTrackIntegration({
            variables: { organizationId: organization.id },
          });
        },
      });
    }

    return result;
  }, [
    organization,
    label,
    orgIntegrationsData,
    slackConfigData,
    removeSlackIntegration,
    removeTeamsIntegration,
    removeGithub,
    removeYouTrackIntegration,
    saveFellowIntegration,
    unlinkOrganizationFromIntegration,
  ]);

  // ---------------------------------------------------------------------------
  // Build connected merge integrations list
  // ---------------------------------------------------------------------------
  const connectedMergeIntegrations: MergeIntegration[] = useMemo(
    () =>
      (mergeIntegrationsData?.mergeDevIntegrations ?? [])
        .filter((i): i is NonNullable<typeof i> => i !== null)
        .map((integration) => ({
          id: integration.id,
          type: "merge" as const,
          name:
            integration.integrationDisplayName ||
            integration.integrationName ||
            "Unknown",
          slug: integration.integrationName || "",
          logo: integration.iconUrl || integration.fullImageUrl || "",
          category: integration.category,
          connectedAt: integration.createdAt,
        })),
    [mergeIntegrationsData],
  );

  const allConnectedIntegrations: ConnectedIntegration[] = useMemo(
    () => [...connectedNativeIntegrations, ...connectedMergeIntegrations],
    [connectedNativeIntegrations, connectedMergeIntegrations],
  );

  const mergeDevEnabled = waffle.flag_is_active("merge_dev_enabled");

  const marketplaceCategories = useMemo(
    () =>
      sortIntegrationCategories([
        ...nativeMarketplaceData.categories,
        ...(mergeDevEnabled ? mergeMarketplaceData.categories : []),
      ]),
    [
      mergeDevEnabled,
      mergeMarketplaceData.categories,
      nativeMarketplaceData.categories,
    ],
  );

  const marketplaceCategoryOptions = useMemo(
    () => [ALL_INTEGRATIONS_CATEGORY, ...marketplaceCategories],
    [marketplaceCategories],
  );

  const marketplaceReady =
    nativeMarketplaceData.ready &&
    (!mergeDevEnabled || mergeMarketplaceData.ready);
  const marketplaceVisibleCount =
    nativeMarketplaceData.visibleCount +
    (mergeDevEnabled ? mergeMarketplaceData.visibleCount : 0);
  const showMarketplaceEmptyState =
    marketplaceReady && marketplaceVisibleCount === 0;

  useEffect(() => {
    if (
      activeMarketplaceCategory !== ALL_INTEGRATIONS_CATEGORY &&
      !marketplaceCategories.includes(activeMarketplaceCategory)
    ) {
      setActiveMarketplaceCategory(ALL_INTEGRATIONS_CATEGORY);
    }
  }, [activeMarketplaceCategory, marketplaceCategories]);

  const handleMarketplaceSearchChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setMarketplaceSearchQuery(event.target.value);
      if (event.target.value.trim()) {
        setActiveMarketplaceCategory(ALL_INTEGRATIONS_CATEGORY);
      }
    },
    [],
  );

  const handleNativeMarketplaceDataChange = useCallback(
    (data: MarketplaceData) => {
      setNativeMarketplaceData((current) =>
        isMarketplaceDataEqual(current, data) ? current : data,
      );
    },
    [],
  );

  const handleMergeMarketplaceDataChange = useCallback(
    (data: MarketplaceData) => {
      setMergeMarketplaceData((current) =>
        isMarketplaceDataEqual(current, data) ? current : data,
      );
    },
    [],
  );

  // ---------------------------------------------------------------------------
  // Disconnect handler
  // ---------------------------------------------------------------------------
  const handleDisconnect = useCallback(
    (integration: ConnectedIntegration) => {
      if (integration.type === "native" && integration.onDisconnect) {
        integration.onDisconnect();
      } else if (integration.type === "merge") {
        removeMergeDevIntegration({
          variables: { integrationId: integration.id },
        });
      }
    },
    [removeMergeDevIntegration],
  );

  const slackIndexingConfig = slackConfigData?.organization
    ?.slackIndexingConfiguration as
    | {
        hasNotificationPermissions?: boolean;
        hasBotPermissions?: boolean;
        hasBotContextPermissions?: boolean;
        hasDataIngestionPermissions?: boolean;
      }
    | undefined;

  const needsBotContextReconnect =
    (slackIndexingConfig?.hasBotPermissions ?? false) &&
    !(slackIndexingConfig?.hasBotContextPermissions ?? false);

  const getExistingSlackPermissions = useCallback((): SlackPermissions => {
    return {
      notifications: slackIndexingConfig?.hasNotificationPermissions ?? false,
      bot: slackIndexingConfig?.hasBotPermissions ?? false,
      ingest: slackIndexingConfig?.hasDataIngestionPermissions ?? false,
    };
  }, [slackIndexingConfig]);

  // ---------------------------------------------------------------------------
  // Slack connect handler
  // ---------------------------------------------------------------------------
  const handleSlackConnect = useCallback(() => {
    setSlackPermissions(getExistingSlackPermissions());
    setShowSlackPermissionsDialog(true);
  }, [getExistingSlackPermissions]);

  const buildSlackConnectUrl = useCallback(
    (permissions: SlackPermissions) => {
      const perms = Object.entries(permissions)
        .filter(([, enabled]) => enabled)
        .map(([key]) => key)
        .join(",");
      const separator = slackConnectUrl.includes("?") ? "&" : "?";
      return perms.length > 0
        ? `${slackConnectUrl}${separator}perms=${encodeURIComponent(perms)}`
        : slackConnectUrl;
    },
    [slackConnectUrl],
  );

  const handleSaveFellowIntegration = useCallback(
    (apiKey: string, apiDomain: string) => {
      saveFellowIntegration({
        variables: {
          organizationId: organization.id,
          fellowApiKey: apiKey,
          fellowApiDomain: apiDomain,
          fellowTranscriptSyncEnabled: true,
        },
        onCompleted: () => {
          successNotificationVar({ title: "Fellow integration saved" });
          setShowFellowIntegrationDialog(false);
        },
        onError: (error) => {
          errorNotificationVar({ title: error.message });
        },
      });
    },
    [organization.id, saveFellowIntegration],
  );

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div>
      <div className="pb-4">
        <h2 className="text-2xl font-medium text-gray-800">
          Organization Integrations
        </h2>
        <div className="text-sm">
          You can also{" "}
          <Link
            to="/account/integrations"
            className="font-medium text-blue-600 hover:underline"
          >
            set up personal integrations
          </Link>
        </div>
      </div>

      {needsBotContextReconnect && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 flex items-start gap-3">
          <ExclamationIcon className="size-5 text-amber-600 mt-0.5 shrink-0" />
          <div className="flex-1 space-y-1">
            <p className="text-sm font-semibold text-gray-900">
              Slack bot context is limited
            </p>
            <p className="text-sm text-gray-700">
              Reconnect Slack so the Topicflow bot can read prior channel
              messages when mentioned.
            </p>
          </div>
          <Button
            theme={buttonTheme.primary}
            size={ButtonSize.medium}
            onClick={handleSlackConnect}
          >
            Update Permissions
          </Button>
        </div>
      )}

      {/* Connected Integrations Table */}
      <IntegrationsConnectedTable
        integrations={allConnectedIntegrations}
        onDisconnect={handleDisconnect}
        onRefetch={handleRefetch}
      />

      {/* Marketplace Section */}
      <div className="space-y-4">
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-2">
            {marketplaceCategoryOptions.map((category) => {
              const isActive = category === activeMarketplaceCategory;
              return (
                <button
                  key={category}
                  type="button"
                  className={classNames(
                    "h-8 rounded-full border px-3 text-sm font-medium transition-colors",
                    isActive
                      ? "border-blue-500 bg-blue-50 text-blue-700"
                      : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50",
                  )}
                  onClick={() => setActiveMarketplaceCategory(category)}
                >
                  {category === ALL_INTEGRATIONS_CATEGORY
                    ? "All"
                    : getIntegrationCategoryLabel(category)}
                </button>
              );
            })}
          </div>
          <label className="relative block w-full sm:max-w-xs">
            <span className="sr-only">Search integrations</span>
            <SearchIcon className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-gray-400" />
            <input
              value={marketplaceSearchQuery}
              onChange={handleMarketplaceSearchChange}
              placeholder="Search integrations..."
              className="h-9 w-full rounded-md border border-gray-200 bg-white pl-9 pr-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </label>
        </div>

        <div
          className={classNames(
            "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4",
            showMarketplaceEmptyState && "hidden",
          )}
        >
          <IntegrationsMarketNative
            organization={organization}
            connectedNativeIntegrations={connectedNativeIntegrations}
            activeCategory={activeMarketplaceCategory}
            searchQuery={marketplaceSearchQuery}
            onRefetch={handleRefetch}
            onSlackConnect={handleSlackConnect}
            onFellowConnect={() => setShowFellowIntegrationDialog(true)}
            onMarketplaceDataChange={handleNativeMarketplaceDataChange}
          />
          <IntegrationsMarketMerge
            organization={organization}
            connectedMergeIntegrations={connectedMergeIntegrations}
            activeCategory={activeMarketplaceCategory}
            searchQuery={marketplaceSearchQuery}
            onRefetch={handleRefetch}
            onMarketplaceDataChange={handleMergeMarketplaceDataChange}
          />
        </div>

        {showMarketplaceEmptyState && (
          <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 px-4 py-8 text-center text-sm text-gray-500">
            No integrations match your filters.
          </div>
        )}
      </div>
      <SlackPermissionsDialog
        open={showSlackPermissionsDialog}
        initialPermissions={slackPermissions}
        onClose={() => setShowSlackPermissionsDialog(false)}
        onContinue={(permissions) => {
          setSlackPermissions(permissions);
          setShowSlackPermissionsDialog(false);
          window.location.href = buildSlackConnectUrl(permissions);
        }}
      />
      <FellowIntegrationDialog
        open={showFellowIntegrationDialog}
        initialApiDomain={organization.fellowApiDomain}
        isSaving={isSavingFellowIntegration}
        onClose={() => setShowFellowIntegrationDialog(false)}
        onSave={handleSaveFellowIntegration}
      />
    </div>
  );
};

export default Integrations;
